import { motion as Motion } from 'framer-motion';
import { useAtlasProjection } from '../../components/map/AtlasProjectionContext';
import { SLOT, getBuddyAsset } from '../../lib/buddyManifest';
import s from './CharacterFigurine.module.css';

/**
 * The buddy PNG, anchored with its feet on the current node.
 *
 * Looks up the current node by `currentNodeKey` in the active atlas's nodes and
 * projects its position via the atlas frame context. Renders null when the key
 * has no matching node (e.g. 'level-0' before the first level-up). Slides
 * smoothly between nodes on level-up via framer-motion (animates left/top — the
 * CSS transform that anchors the feet is left intact, so x/y can't be used).
 */
export default function CharacterFigurine({ buddyKey, currentNodeKey }) {
  const { atlas, containerW, projectNode } = useAtlasProjection();
  const node = atlas?.nodes?.find((n) => n.node_key === currentNodeKey);
  const pos = projectNode(node?.src_x ?? 0.5, node?.src_y ?? 0.5, 'bottomCenter');

  // Wait for the frame to be measured before rendering. On the first paint
  // containerW is 0 (ResizeObserver hasn't fired yet) which makes projectNode
  // return {x:0,y:0}. Without this guard the figurine mounts at the top-left
  // corner and springs to the correct position, which looks like a bug.
  if (!node || !containerW) return null;

  // Per-node walk pose (D-4): figurine_view 2 → back-right, anything else → the
  // back-left default. Resolved through the buddy manifest — never hardcoded.
  const view = node.figurine_view === 2 ? 2 : 1;
  const src = getBuddyAsset(buddyKey, view === 2 ? SLOT.FIGURINE_VIEW_2 : SLOT.FIGURINE_VIEW_1);

  return (
    <Motion.div
      className={s.figurine}
      aria-hidden="true"
      initial={false}
      animate={{ left: pos.x, top: pos.y }}
      transition={{ type: 'spring', stiffness: 240, damping: 28 }}
    >
      <img src={src} alt="" />
    </Motion.div>
  );
}
